import PropTypes from 'prop-types';
import React from 'react';

import Category from '../../models/category';
import Contingent from '../../models/contingentModel';
import Inventory from '../../models/inventory';
import EventModel from '../../models/eventModel';
import RateTemplate from '../../models/rateTemplate';
import Form from '../../models/form';
import Loading from '../../components/loading';
import ConfirmModal from '../../components/confirmModal';
import { RateStatus } from './components/rateStatus';
import RateMaxPrice from '../../models/rateMaxPrice';
import QuotasModel from '../../models/quotasModel';

import { Icon, Popup } from 'semantic-ui-react';
import { Tooltip, WeezHeader, Button, Card, Message, OptionalForm, ToastContext, TooltipHelp, ToggleCard } from '@weezevent/nacre';

import { useTranslation } from 'react-i18next';
import { useSegment, useTrackingPage } from '../../components/tracking/trackingProvider';
import useMixpanel from '../../context/useMixpanel';
import Chart from '../../models/chart';
import { Link } from 'react-router-dom';
import { useForm, requiredField } from '../../utils/validators';
import useAppContext from '../../context/useAppContext';
import RateFormInfos from './rateFormInfos';
import RateFormSeating from './rateFormSeating';
import RateFormSettingsEvents from './rateFormSettingsEvents';
import { undefined_or_null_or_value } from '../../utils/tools';

import PassCultureLogo from '../ticket_option/integration/images/passculture.png';

import Styles from '../../styles/rate/rateCreation.module.css';
import ReleaseCard from './createOrUpdate/releaseCard';
import CartCard from './createOrUpdate/cartCard';
import EventSettings from '../../models/eventSettings';
import moment from 'moment-timezone';
import { LANGUAGES } from '../../components/translations/translationModal';
import PassCultureEvent from '../../models/passCultureEvent';
import PageError from '../error/pageError';
import TaxModel from '../../models/taxModel';
import TicketManagement from './components/ticketManagement';
import FormErrorsSummary from '../../components/formErrorsSummary';
import { usePermissions } from '../../user/permissions';
import { RateMembershipInfo } from './components/rateMembershipInfo';
import { initLanguage, handleTranslations, cleanTranslations } from '../event_creation/translationsUtils';
import { getAllTranslationsFromPipe } from '../../components/utils/pipeTranslateConvert';

export function TemplatePopup({ template, toRatePage = true }) {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();

    return (
        <Link target="_blank" to={`/ticket/O${organization.id}/rates/${toRatePage ? template['uuid'] : ''}`}>
            <Popup content={<span>{t('ticket.rates.editTemplate')}</span>} trigger={<Icon name="linkify" />} />
        </Link>
    );
}

TemplatePopup.propTypes = {
    template: PropTypes.object.isRequired,
    toRatePage: PropTypes.bool.isRequired
};

export function InheritedInput({ children, rate, property, onChange = () => {}, formState, linkPosition = 'right' }) {
    const isChecked = React.useMemo(() => {
        return Boolean(formState?.[property]);
    }, [formState, property]);

    const isDisabled = React.useMemo(() => {
        if ((formState?.type === 'free' || (rate?.rate_template?.type === 'free' && !formState?.type)) && property === 'price') {
            return true;
        }
        return rate?.rate_template ? !isChecked : false;
    }, [formState, property, rate, isChecked]);

    const inputValue = React.useMemo(() => {
        if ((formState?.type === 'free' || (rate?.rate_template?.type === 'free' && !formState?.type)) && property === 'price') {
            // @Todo repair here on change price value form if typ$e === free//
            if ((rate?.rate_template && !isChecked ? rate?.rate_template[property] : formState?.[property]) !== 0) {
                // onChange(0);
            }
            return 0;
        }
        let value = rate?.rate_template && !isChecked ? rate?.rate_template[property] : formState?.[property];
        return isNaN(Number(value)) ? value : Number(value);
    }, [formState, property, rate, isChecked]);

    const hideIconLinker = React.useMemo(() => {
        return (formState?.type === 'free' || (rate?.rate_template?.type === 'free' && !formState?.type)) && property === 'price';
    }, [formState, property, rate]);

    React.Children.only(children);
    let cloneChildren = React.cloneElement(children, {
        ...children.props,
        disabled: isDisabled,
        value: inputValue
    });

    const { t } = useTranslation();

    if (!rate?.rate_template || hideIconLinker) {
        return cloneChildren;
    }

    return (
        <div className="inherited-label-value-container">
            {linkPosition === 'right' && cloneChildren}
            <Tooltip triggerOnHover title={<span>{isChecked ? t('ticket.rate_template.link_field_template') : t('ticket.rate_template.unlink_field_template')}</span>}>
                <Icon
                    className="inherit-label-icon"
                    name={isChecked ? 'unlink' : 'linkify'}
                    onClick={() => {
                        if (isChecked) {
                            onChange(null);
                        } else {
                            onChange(rate.rate_template[property]);
                        }
                    }}
                />
            </Tooltip>
            {linkPosition === 'left' && cloneChildren}
        </div>
    );
}

InheritedInput.propTypes = {
    rate: PropTypes.object.isRequired,
    property: PropTypes.string.isRequired,
    children: PropTypes.node,
    onChange: PropTypes.func,
    formState: PropTypes.object
};

export default function RateForm({ organizationId, event, rateId, history, RateClass, GroupClass }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const mixpanel = useMixpanel();
    const [{ organization }] = useAppContext();
    const [rates, setRates] = React.useState([]);
    const [rateTemplates, setRateTemplates] = React.useState(null);
    const permissions = usePermissions();

    const toast = React.useContext(ToastContext);

    const [charts, setCharts] = React.useState([]);
    const [inventories, setInventories] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [events, setEvents] = React.useState([]);
    const [groups, setGroups] = React.useState([]);
    const [rate, setRate] = React.useState(null);
    const [contingents, setContingents] = React.useState([]);
    const [forms, setForms] = React.useState([]);
    const [settings, setSettings] = React.useState(null);
    const [isLoading, setLoading] = React.useState(true);
    const [formerGroupId, setGroupId] = React.useState(null);
    const [selectedEvents, setSelectedEvents] = React.useState([]);
    const [showConfirmModal, setShowConfirmModal] = React.useState(false);
    const [hasRateTemplateId, setHasRateTemplateId] = React.useState(false);
    const [salesStart, setSalesStart] = React.useState(null);
    const [openOptionalForm, setOpenOptionalForm] = React.useState(false);
    const [isPassCultureEventActivated, setIsPassCultureEventActivated] = React.useState(false);
    const [pageErrorCode, setPageErrorCode] = React.useState(null);
    const [errorsSummary, setErrorsSummary] = React.useState({});

    useTrackingPage(`VIEW - ${rateId ? 'EDIT' : 'CREATE'} RATE`, { 'Page Title': 'RATE' });

    const languageOptions = React.useMemo(() => {
        let list = event ? event : organization;
        return list.languages.map(lang => {
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: lang
            };
        });
    }, [organization, event, rate]);

    const defaultLanguage = React.useMemo(() => {
        let defaultLocale = event ? event.default_language : organization.default_locale.replace('_', '-').toLowerCase();
        return defaultLocale || languageOptions[0]?.value;
    }, [organization, languageOptions, event]);

    const [taxes, ,] = TaxModel.useApiModel({});

    RateMaxPrice.useApiModel(
        {
            eventId: event ? event.pk : null,
            organizationId: organizationId,
            launch: organizationId && event !== undefined,
            onSuccess: result => {
                onChangeForm('maxPrice', result.max_price);
            }
        },
        [event, organizationId]
    );

    const validateDate = React.useCallback(
        (name, date, form) => {
            let tmpStart = form.visibilityStartDate ? moment.tz(form.visibilityStartDate, event.timezone) : null;
            let tmpEnd = form.visibilityEndDate ? moment.tz(form.visibilityEndDate, event.timezone) : null;
            if (tmpStart && tmpEnd && tmpStart.isAfter(tmpEnd)) {
                return 'ticket.rates.optionalSettings.release.startDateBeforeEndDate';
            }
            return true;
        },
        [event]
    );

    const validateLimitsQuantity = React.useCallback(
        (name, quantity, form) => {
            // validation of min and max quantity in a cart
            let tmpMax = form.maxQuantity;
            let tmpMin = form.minQuantity;
            // TODO add validator no null
            if (tmpMax && tmpMin && tmpMin > tmpMax) {
                return t('ticket.rates.optionalSettings.release.minGreaterThanMax');
            }
            return true;
        },
        [event]
    );

    // In a rate template context, there is no event_id so this request must be skipped
    const [quotas, loadingQuotas] = QuotasModel.useApiModel(
        {
            organizationId: organizationId,
            eventId: event?.pk,
            getForRates: true,
            cache: false,
            allow_cache: false,
            query: {
                id_date: 0
            },
            launch: Boolean(event)
        },
        [organizationId, event]
    );
    const EventQuotaTaken = React.useMemo(() => quotas?.find(quota => quota.rate_id === 0 && quota.event_id === event.id && quota.session_id === 0)?.quota_taken ?? 0, [
        quotas,
        rate
    ]);

    const currentQuotaTaken = React.useMemo(() => {
        return quotas?.find(quota => quota.rate_id === rate?.id && quota.event_id === event.id && quota.session_id === 0)?.quota_taken ?? 0;
    }, [quotas, rate]);

    const validateQuantity = React.useCallback(
        (name, value) => {
            if (value && settings?.quota && value > settings?.quota) return t('ticket.rates.infos.quantityExceed');
            if (value && value !== 0 && value < currentQuotaTaken) return t('ticket.rates.infos.invalidQuantity');
            return true;
        },
        [event, settings, currentQuotaTaken, EventQuotaTaken]
    );

    // Inventory and Category are mandatory for seated events without sessions
    const validateOnlySeated = React.useCallback(
        (name, value, form) => {
            if (event?.use_sessions && name === 'inventories') {
                return true;
            }
            if (!event || form.rate_template_id) {
                return true;
            }
            if (((event.chart && event.is_seated) || (event?.isFakeSeating() && (inventories?.length > 1 || categories?.length > 1))) && !value?.length) {
                return t('ticket.common.required');
            }
            return true;
        },
        [event, inventories, categories]
    );

    const validateName = React.useCallback(
        (name, value, form) => {
            return rate?.rate_template && form.rate_template_id ? true : requiredField('name', form.name, form);
        },
        [rate]
    );

    const validatePrice = React.useCallback(
        (name, value, form) => {
            const currency = organization.currency;
            if (!currency) {
                return 'Currency not found';
            }
            const maxPrice = new Intl.NumberFormat(event?.default_language, { style: 'currency', currency: currency }).format(form?.maxPrice);
            const minPrice = new Intl.NumberFormat(event?.default_language, { style: 'currency', currency: currency }).format(1);

            if (value > form?.maxPrice) {
                return t('ticket.rates.infos.rateError', { amount: maxPrice });
            }

            if (form.type !== 'free' && value < 1 && !form.rate_template_id) {
                return t('ticket.rates.infos.rateMinError', { amount: minPrice });
            }

            return true;
        },
        [event]
    );

    const validators = React.useMemo(() => {
        return {
            quantity: event ? validateQuantity : () => true,
            visibilityStartDate: validateDate,
            visibilityEndDate: validateDate,
            maxQuantity: validateLimitsQuantity,
            minQuantity: validateLimitsQuantity,
            inventories: validateOnlySeated,
            category: validateOnlySeated,
            name: validateName,
            price: validatePrice
        };
    }, [validateQuantity, validateDate, validateLimitsQuantity, validateOnlySeated, validateName]);

    const [formState, setForm, setFormObject, formErrors, formValidate] = useForm(
        {
            name: '',
            type: null,
            price: 0,
            quantity: null,
            tax_id: null,
            form_id: null,
            disabled: false,
            description: '',
            inventories: null,
            category: null,
            contingents: null,
            group: null,
            fees: [],
            maxPrice: 3000,
            maxQuantity: null,
            minQuantity: null,
            bundleSize: 1,
            defaultQuantity: null,
            visibilityStartDate: null,
            visibilityEndDate: null,
            relays: [],
            is_passculture: false,
            translations: {},
            transfer_settings: null
        },
        validators
    );

    const fieldsNamesTradsErrors = React.useMemo(() => {
        return {
            quantity: t('ticket.rates.infos.quantity'),
            visibilityStartDate: t('ticket.rates.modalSettingsEvent.startSales'),
            visibilityEndDate: t('ticket.rates.modalSettingsEvent.endSales'),
            maxQuantity: t('ticket.rates.optionalSettings.cart.maxQuantity'),
            minQuantity: t('ticket.rates.optionalSettings.cart.minQuantity'),
            inventories: t(event?.isFakeSeatingMultipleInventories() ? 'ticket.rates.passculture.fakeSeating.inventoryTitle' : 'ticket.rates.inventories'),
            category: t('ticket.rates.category'),
            name: t('ticket.rates.infos.name'),
            price: t(`ticket.rates.infos.total.total-buyer-selling-price${formState.type === 'open' ? '-open' : ''}`)
        };
    }, [event, formState]);

    const onChangeForm = React.useCallback(
        (fieldName, value) => {
            const defaultOptions = {
                disableValidation: true,
                cleanErrors: true
            };

            setForm(fieldName, value, defaultOptions);
        },
        [setForm]
    );

    const onChangeFormObject = React.useCallback(
        (obj, merge = false) => {
            const defaultOptions = {
                cleanErrors: true
            };

            setFormObject(obj, merge, defaultOptions);
        },
        [setFormObject]
    );

    const RATE_TYPES = {
        fixed: {
            type: 'fixed',
            price: 10,
            name: t('ticket.creationEvent.rates.priceNamePaid')
        },
        free: {
            type: 'free',
            price: 0,
            name: t('ticket.creationEvent.rates.priceNameFree')
        },
        open: {
            type: 'open',
            price: 10,
            name: t('ticket.creationEvent.rates.priceNameDonation')
        }
    };

    React.useEffect(() => {
        window.scrollTo({ top: 0 });
        setLoading(true);

        let ratePromise = null;
        let categoryPromise = null;
        let inventoryPromise = null;
        let contingentPromise = null;
        let chartPromise = null;
        let rateTemplatePromise = null;
        let formPromise = null;
        let settingsPromise = null;
        let rateGroupPromise = GroupClass.getAll({ organizationId, eventId: event ? event.id : null });
        let ratesPromise = RateClass.getAll({ organizationId, eventId: event ? event.id : null });
        if (rateId) {
            ratePromise = RateClass.get({ id: rateId, organizationId, eventId: event ? event.id : null }, { expand: 'relays' });
        }

        if (!event) {
            chartPromise = Chart.getAll({ organizationId, query: { only_seated: true } });

            let eventElements = EventModel.getAll({ organizationId, query: { include_deleted: true } });
            Promise.all([eventElements])
                .then(([eventElements]) => {
                    setEvents(eventElements.sort((el1, el2) => new Date(el2.start_date).getTime() - new Date(el1.start_date).getTime()));
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.common.toast_error'));
                });
        }

        if (event) {
            rateTemplatePromise = RateTemplate.getAll({ organizationId });
            formPromise = Form.getAll({ organizationId, eventId: event.id });
            settingsPromise = EventSettings.get({ id: null, organizationId, eventId: event.id });
        }

        if (event?.isSeatedForRates()) {
            categoryPromise = Category.getAll({ organizationId, chartId: event.chart });
            inventoryPromise = Inventory.getAll({ organizationId, chartId: event.chart });
            contingentPromise = Contingent.getAll({ organizationId, eventId: event.id });
        }

        Promise.all([
            ratePromise,
            settingsPromise,
            categoryPromise,
            inventoryPromise,
            rateGroupPromise,
            contingentPromise,
            ratesPromise,
            chartPromise,
            rateTemplatePromise,
            formPromise
        ])
            .then(
                ([
                    rateElement,
                    settingsElement,
                    categoriesElements,
                    inventoriesElements,
                    rateGroups,
                    contingentsElements,
                    ratesElements,
                    charts,
                    rateTemplatesElements,
                    formElements
                ]) => {
                    let tmpInventories = [];
                    let tmpCategories = [];
                    if (event?.isSeatedForRates()) {
                        tmpInventories = inventoriesElements.filter(inventory => inventory.event === event.id);
                        tmpCategories = categoriesElements;
                        setInventories(tmpInventories);
                        setCategories(categoriesElements?.sort((a, b) => a.name.localeCompare(b.name)));
                        setContingents(contingentsElements);
                    }

                    // Case rate edition
                    if (rateId) {
                        setGroupId(rateElement.group);

                        const nameOrTemplate = rateElement.name || rateElement.rate_template.name;
                        const allLang = event?.languages || organization.languages;
                        const transName = getAllTranslationsFromPipe(nameOrTemplate, defaultLanguage, allLang);
                        const transDescription = getAllTranslationsFromPipe(rateElement.description, defaultLanguage, allLang);

                        const defaultDescription = transDescription[defaultLanguage] || rateElement.description;
                        const defaultName = transName[defaultLanguage] || nameOrTemplate;

                        const _trans = {
                            ...rateElement.translations,
                            name: {
                                //translations from pipetranslate
                                ...cleanTranslations(transName, defaultLanguage, Object.keys(rateElement?.translations?.name || {})),
                                //from rateElement.translations
                                ...cleanTranslations(rateElement?.translations?.name, defaultLanguage, Object.keys(rateElement?.translations?.name || {}))
                            },
                            description: {
                                //translations from pipetranslate
                                ...cleanTranslations(transDescription, defaultLanguage, Object.keys(rateElement?.translations?.description || {})),
                                //from rateElement.translations
                                ...cleanTranslations(rateElement?.translations?.description, defaultLanguage, Object.keys(rateElement?.translations?.description || {}))
                            }
                        };

                        let form = {
                            name: defaultName,
                            price: rateElement.price,
                            group: rateElement.group,
                            form_id: rateElement.form, //when pyvar will send form object , put form.id here
                            contingents: rateElement.contingents || [],
                            inventories: null,
                            category: rateElement.category,
                            type: rateElement.type,
                            quantity: rateElement.quantity,
                            tax_id: rateElement.tax_id,
                            disabled: rateElement.disabled,
                            description: defaultDescription,
                            fees: rateElement.fees || [],
                            maxQuantity: rateElement.cart_max_quantity,
                            minQuantity: rateElement.cart_min_quantity,
                            bundleSize: rateElement.bundle_size,
                            defaultQuantity: rateElement.preselected_quantity,
                            visibilityStartDate: rateElement.visibility_start_date,
                            visibilityEndDate: rateElement.visibility_end_date,
                            relays: rateElement.relays?.map(relay => relay.id),
                            translations: _trans,
                            is_passculture: rateElement.is_passculture,
                            transfer_settings: rateElement.transfer_settings ? rateElement.transfer_settings : null
                        };

                        if (rateElement.category && tmpCategories.find(cat => cat.pk === rateElement.category)?.general_admission) {
                            form.contingents = [];
                        }

                        if (rateElement.rate_template) {
                            form.rate_template_id = rateElement.rate_template.pk;
                            setHasRateTemplateId(true);
                        }

                        if (rateElement.inventories?.length) {
                            form.inventories = event?.use_sessions ? rateElement.inventories : rateElement.inventories[0];
                        }

                        //Put seats objects automatically if there is only one object
                        if (tmpInventories.length === 1 && !rateElement.rate_template && !rateElement.inventories?.length) {
                            form.inventories = tmpInventories[0].pk;
                        }

                        //Put seats objects automatically if there is only one object
                        if (tmpCategories.length === 1 && !rateElement.rate_template && !rateElement.category) {
                            form.category = tmpCategories[0].pk;
                        }

                        onChangeFormObject(form, true);
                        // use default from pipe
                        rateElement.name = defaultName;
                        setRate(rateElement);
                        setSalesStart(form.visibilityStartDate ? 'date' : form.relays?.length ? 'relays' : null);
                        setOpenOptionalForm(
                            Boolean(
                                form.relays?.length ||
                                    form.visibilityEndDate ||
                                    form.visibilityStartDate ||
                                    form.defaultQuantity ||
                                    form.bundleSize !== 1 ||
                                    form.minQuantity ||
                                    form.maxQuantity ||
                                    form.transfer_settings
                            )
                        );
                    } else {
                        // Case rate creation
                        let newRate = new RateClass();
                        let translationsDefaultValue = { name: {}, description: {}, ...formState.translations };
                        let form = { ...formState, ...RATE_TYPES.fixed, translations: translationsDefaultValue };

                        //Put seats objects automatically if there is only one object
                        if (tmpInventories.length === 1) {
                            form.inventories = tmpInventories[0].pk;
                        }
                        if (tmpCategories.length === 1) {
                            form.category = tmpCategories[0].pk;
                        }
                        onChangeFormObject(form);
                        setRate(newRate);
                    }

                    setRateTemplates(rateTemplatesElements);
                    setForms(formElements || []);

                    RateClass.sort(ratesElements);
                    setRates(ratesElements);

                    setGroups(rateGroups);
                    setSettings(settingsElement);

                    let categoriesPromises = [];
                    let contingentsPromises = [];

                    //Get all categories and all contigents for case rate_template
                    if (!event) {
                        charts.forEach(chart => {
                            let categoryPromise = Category.getAll({
                                organizationId,
                                chartId: chart.pk
                            });
                            categoriesPromises.push(categoryPromise);

                            let contingentPromise = Contingent.getAll({
                                organizationId: organizationId,
                                chartId: chart.pk
                            });
                            contingentsPromises.push(contingentPromise);
                        });
                    }

                    let allCategories = Promise.all(categoriesPromises);
                    let allContingents = Promise.all(contingentsPromises);

                    return Promise.all([charts, allCategories, allContingents]);
                }
            )
            .then(([charts, categories, contingents]) => {
                setCharts(charts);

                categories.forEach((cats, i) => {
                    cats.forEach(category => {
                        category.chart = charts[i].uuid;
                    });
                });
                if (!event) {
                    setContingents(contingents.flat(2));
                    setCategories(categories.flat(2).sort((a, b) => a.name.localeCompare(b.name)));
                }
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.common.toast_error'));
                setPageErrorCode(e?.code || 500);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [defaultLanguage]);

    // eslint-disable-next-line
    const [_] = PassCultureEvent.useApiModel(
        {
            organizationId: organization.id,
            eventId: event?.id,
            launch: Boolean(event?.isFakeSeating()),
            onSuccess: result => {
                if (result?.response?.id) {
                    setIsPassCultureEventActivated(true);
                }
            }
        },
        [organization, event]
    );

    const prefixUrl = React.useCallback(() => {
        return event ? `/ticket/O${organizationId}/E${event.id}/settings/rates` : `/ticket/O${organizationId}/rates`;
    }, [organizationId, event]);

    const handleDisplayOrder = React.useCallback(
        rate => {
            //Set display_order of the current rate
            let lengthGroup = rates.filter(frate => frate.group === rate.group).length || 0;
            rate.display_order = lengthGroup;
            //Set display_order of the other rates from the former group
            let tmpGroup = rates.filter(frate => frate.group === formerGroupId && frate.pk !== rate.pk);
            //Set the new display order if different
            let editedRates = [];
            tmpGroup.forEach((frate, i) => {
                if (frate.display_order !== i) {
                    frate.display_order = i;
                    frate.translations = { name: {} }; //handle translations issue
                    editedRates.push(frate);
                }
            });

            if (!editedRates.length) {
                return rate.display_order;
            }

            RateClass.save(editedRates, {
                organizationId,
                eventId: event ? event.id : null
            }).catch(e => {
                toast.error(t('ticket.common.toast_error'));
            });

            return rate.display_order;
        },
        [rates, organizationId, event]
    );

    const taxToUse = React.useMemo(() => {
        if (!taxes || (formState.tax_id == null && (!event || event.tax_id == null))) return { id: null, value: 0 };
        let foundTax = taxes.find(t => t.id === formState.tax_id);
        //we try using the event tax if no tax selected
        if (!foundTax?.tax_values) {
            foundTax = taxes.find(t => t.id === event.tax_id);
        }
        return {
            id: foundTax ? foundTax.id : null,
            value: foundTax?.tax_values ? foundTax.tax_values.tax_value + foundTax.tax_values.tax_supplemental_value : 0
        };
    }, [taxes, event?.tax_id, formState.tax_id]);

    const includeTaxInPrice = React.useCallback(
        (withTaxes, value) => {
            const finalPrice = withTaxes ? value * (1 + taxToUse.value / 100) : value / (1 + taxToUse.value / 100);
            //cap to two digits so the division doesn't fill the field
            return +finalPrice.toFixed(2);
        },
        [taxToUse]
    );

    const handleSubmit = React.useCallback(() => {
        // validate data
        if (!formValidate(true, setErrorsSummary)) {
            return toast.error(t('ticket.common.errorFields'));
        }
        setLoading(true);
        formState.fees.forEach(fee => {
            if (!fee.name) {
                fee.name = t('ticket.rates.infos.fees.service_fee');
            }
        });

        let _fees = formState.fees.filter(fee => undefined_or_null_or_value(fee.amount) !== null);
        // If we only update the VAT, without changing the value of one or more charges,
        // we need to update the amount excluding VAT of each fee.
        _fees = _fees.map(fee => ({ ...fee, amount_no_tax: String(includeTaxInPrice(false, fee.amount)) }));

        let _inventories = formState.inventories || [];
        // make sure inventories is an array
        _inventories = Array.isArray(_inventories) ? _inventories : new Array(_inventories);

        if (!formState.transfer_settings?.allow_tickets_scanned && !formState.transfer_settings?.attendee_validation_required && !formState.transfer_settings?.allow_force_cancel) {
            onChangeForm('transfer_settings', {});
        }

        let rateTmp = {
            uuid: rateId,
            id: rateId,
            inventories: _inventories,
            name: formState.name,
            price: formState.price,
            contingents: formState.contingents || [],
            category: formState.category,
            group: formState.group,
            form_id: formState.form_id,
            event_ids: selectedEvents,
            rate_template_id: formState.rate_template_id,
            type: formState.type,
            quantity: formState.quantity,
            tax_id: formState.tax_id,
            disabled: formState.disabled,
            description: formState.description,
            fees: _fees,
            cart_max_quantity: formState.maxQuantity,
            cart_min_quantity: formState.minQuantity,
            bundle_size: formState.bundleSize,
            preselected_quantity: formState.defaultQuantity,
            visibility_start_date: formState.visibilityStartDate,
            visibility_end_date: formState.visibilityEndDate,
            relays: formState.relays,
            is_passculture: formState.is_passculture,
            transfer_settings: formState.transfer_settings
        };

        if (formerGroupId !== rateTmp.group || !rateId) {
            rateTmp.display_order = handleDisplayOrder(rateTmp);
        }

        if (RateClass !== RateTemplate) {
            rateTmp['translations'] = formState.translations;
        }

        const rateObject = new RateClass(rateTmp);
        rateObject
            .save({
                organizationId,
                eventId: event ? event.id : null
            })
            .then(() => {
                const trackingData = {
                    'Page Title': 'RATE',
                    'Activate Pass Culture': rateObject.is_passculture
                };
                mixpanel.track('ACTION > VALIDATE RATE', trackingData);
                toast.success(t(`ticket.rates.${rateId ? 'editSuccess' : 'creationSuccess'}`));
                history.push(prefixUrl());
            })
            .catch(e => {
                toast.error(t(`ticket.rates.${rateId ? 'editError' : 'creationError'}`));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [organizationId, formState, mixpanel, event, RateClass, formValidate, selectedEvents, handleDisplayOrder, rate, includeTaxInPrice]);

    const initLanguageRate = React.useCallback((value, name) => initLanguage(value, name, formState, onChangeFormObject), [formState, onChangeFormObject]);

    const handleTranslationsRate = React.useCallback(
        (value, property, selectedLanguage) => handleTranslations(value, property, selectedLanguage, formState, onChangeFormObject, defaultLanguage, rate?.translations),
        [formState, onChangeFormObject, defaultLanguage, rate]
    );

    const labelBodyConfirmModal = React.useMemo(() => {
        //Case create or update with selectedEvents
        if (selectedEvents.length) {
            return t('ticket.rates.confirmModal.labelBodyWithSelectedEvents', { name: formState.name, count: selectedEvents.length });
        }

        if (event && !formState?.rate_template_id) {
            return t('ticket.rates.confirmModal.labelBodyUnlinkTemplate');
        }

        //Case create without selectedEvents
        if (!rateId) {
            return t('ticket.rates.confirmModal.labelBodyWithoutSelectedEvents');
        }

        return '';
    }, [selectedEvents, rateId, formState, event]);

    const onChangeEvents = React.useCallback(selected => {
        setSelectedEvents(selected.map(event => event.id));
    }, []);

    const handleRateLinker = React.useCallback(
        rate_template_id => {
            if (!rate_template_id && !formState?.rate_template_id) {
                return null;
            }
            //Null ? copy the data and link it
            let tmpForm = { ...formState };
            //Case 1: From rate_template to null, we copy the value
            if (!rate_template_id && formState?.rate_template_id) {
                let rate_template = rateTemplates.find(r => r.pk === formState.rate_template_id);
                if (!rate_template) {
                    return;
                }
                for (let key of Object.keys(formState)) {
                    tmpForm[key] = formState[key] ?? rate_template[key] ?? null;
                }
            }
            // //Case 2: We add a rate_template_id and we didn't have one before
            let new_rate_template = rateTemplates.find(r => r.pk === rate_template_id);
            if (rate_template_id && !formState?.rate_template_id) {
                for (let key of Object.keys(new_rate_template.object)) {
                    if (formState[key] === new_rate_template[key] && Object.prototype.hasOwnProperty.call(formState, key)) {
                        tmpForm[key] = new_rate_template[key];
                    }
                }
            }

            tmpForm.rate_template_id = rate_template_id;

            const selectedGroupId = formState.group || groups.find(group => group.group_template_id === new_rate_template?.group)?.id || null;
            onChangeFormObject({
                ...tmpForm,
                group: selectedGroupId
            });

            if (!tmpForm?.rate_template_id) {
                rate.rate_template = null;
            } else {
                rate.rate_template = rateTemplates.find(r => r.pk === tmpForm.rate_template_id);
            }
            let newRate = new RateClass(rate);
            setRate(newRate);
        },
        [rate, formState, rateTemplates, groups]
    );

    const displayModalOrSubmit = React.useCallback(() => {
        if (!event || (hasRateTemplateId && !rate?.rate_template)) {
            return setShowConfirmModal(true);
        }
        return handleSubmit();
    }, [event, hasRateTemplateId, rate, handleSubmit]);

    const rateLimit = React.useMemo(() => {
        if (!event) {
            return false;
        }
        return formState.price > formState.maxPrice;
    }, [formState, event]);

    const isAllowedTicketManagement = React.useMemo(() => {
        return permissions.hasCapacities(['PyvarCapacity.RATE_EXCHANGE_ADMIN', 'PyvarCapacity.RATE_EXCHANGE'], organization.id) && RateClass !== RateTemplate && !rate?.distributor;
    }, [permissions, organization, rate, RateClass, RateTemplate]);

    const rateNameTooLong = React.useMemo(() => {
        return formState.is_passculture && formState.name.length > 50;
    }, [formState]);

    const isDuplicatePassCultureName = React.useMemo(() => {
        if (!formState.is_passculture) return false;

        const currentName = formState.name?.trim().toLowerCase();
        return rates.some(rate => rate.is_passculture && String(rate.id) !== rateId && rate.name?.trim().toLowerCase() === currentName);
    }, [rates, formState, rateId]);

    const cardViewPermissions = React.useMemo(() => {
        return {
            ticketManagement: Boolean(rateId && permissions.hasCapacities(['PyvarCapacity.PERSONALSPACE', 'PyvarCapacity.PERSONALSPACE_ADMIN'], organization?.id))
        };
    }, [organization?.id, permissions]);

    if (!isLoading && (pageErrorCode || !rate)) {
        let backHomeUrl = event ? `/ticket/O${organizationId}/E${event.id}/settings/rates` : `/ticket/O${organizationId}/events`;
        return <PageError errorCode={pageErrorCode || 500} backHomeUrl={backHomeUrl} />;
    }

    if (isLoading || (event && loadingQuotas)) {
        return <Loading />;
    }

    function HelpTitle() {
        return (
            <>
                {t('ticket.rates.passculture.title')}
                <TooltipHelp title={t('ticket.rates.passculture.tooltip')} mini position={'top'} />
            </>
        );
    }

    return (
        <div className={Styles['rate-creation-container']}>
            {showConfirmModal && (
                <ConfirmModal
                    labelTitle={t(`ticket.rates.modal-confirm-${RateClass === RateTemplate ? 'link' : 'unlink'}.title`)}
                    objectToSave={formState}
                    onClose={() => setShowConfirmModal(false)}
                    onAccept={handleSubmit}
                    labelBody={labelBodyConfirmModal}
                />
            )}
            <WeezHeader
                title={
                    <div className={Styles['rate-title-container']}>
                        {rateId ? (
                            <>
                                <span>{rate?.name || rate?.rate_template?.name}</span>
                                <RateMembershipInfo rate={rate} detail={true} />
                                <span className={Styles['rate-distributor']}>
                                    {rate?.distributor ? (
                                        <span>({rate?.distributor?.label})</span>
                                    ) : (
                                        // In a rate template context, there is no event or quotas to display
                                        event && <RateStatus quotas={quotas} rate={rate} ratesList={rates} settings={settings} event={event} />
                                    )}
                                </span>
                            </>
                        ) : (
                            t('ticket.rates.newRate')
                        )}
                    </div>
                }
                backLink={{
                    onClick: () => {
                        history.push(prefixUrl());
                        track('ACTION > BACKHEADER', { 'Page Title': 'NEW RATE' });
                    }
                }}
                rightComponent={[
                    <Button
                        primary
                        key={'submit'}
                        onClick={() => {
                            displayModalOrSubmit();
                        }}
                        label={t(`ticket.common.action.save`)}
                        isLoading={isLoading}
                    />
                ]}
            />
            <FormErrorsSummary formErrors={errorsSummary} fieldsTrads={fieldsNamesTradsErrors} />
            {isPassCultureEventActivated && (rateNameTooLong || isDuplicatePassCultureName) && (
                <Message type="warning" title={t('ticket.common.warning')}>
                    {rateNameTooLong && <div>• {t('ticket.rates.infos.warning-message.name-too-long')}</div>}
                    {isDuplicatePassCultureName && <div>• {t('ticket.rates.infos.warning-message.duplicated-rate-name')}</div>}
                </Message>
            )}
            <Card.FormManager useSteps={false}>
                <Card.FormStep
                    title={t(`ticket.rates.infos.title`)}
                    subtitle={t('ticket.rates.infos.subtitle')}
                    rightComponent={
                        <Message type="tips" title={t('ticket.common.tips')} mini>
                            {t('ticket.rates.infos.tips')}
                        </Message>
                    }
                >
                    <>
                        <RateFormInfos
                            organizationId={organizationId}
                            event={event}
                            organization={organization}
                            defaultLanguage={defaultLanguage}
                            languageOptions={languageOptions}
                            minimumQuota={currentQuotaTaken}
                            rate={rate}
                            rateLimit={rateLimit}
                            form={formState}
                            formErrors={formErrors}
                            rateTemplates={rateTemplates}
                            onChangeForm={onChangeForm}
                            onChangeFormObject={onChangeFormObject}
                            groups={groups}
                            forms={forms}
                            handleRateLinker={handleRateLinker}
                            rateTypes={RATE_TYPES}
                            taxes={taxes}
                            includeTaxInPrice={includeTaxInPrice}
                            taxIdToUse={taxToUse.id}
                            handleTranslationsRate={handleTranslationsRate}
                            initLanguageRate={initLanguageRate}
                        />
                        {rate && !rate?.distributor && isPassCultureEventActivated && (
                            <ToggleCard
                                title={<HelpTitle />}
                                subtitle={t('ticket.rates.passculture.subtitle')}
                                logoURL={PassCultureLogo}
                                key={'passculture'}
                                toggle={formState.is_passculture}
                                onToggle={value => onChangeForm('is_passculture', value)}
                            />
                        )}
                    </>
                </Card.FormStep>
                {((rate && !rate?.distributor && charts?.length > 0 && !event) ||
                    event?.is_seated ||
                    (event?.isFakeSeating() && (inventories?.length > 1 || categories?.length > 1))) && (
                    <Card.FormStep
                        title={t(event?.isFakeSeatingMultipleInventories() ? 'ticket.rates.passculture.fakeSeating.title' : 'ticket.rates.seating.title')}
                        subtitle={t(event?.isFakeSeatingMultipleInventories() ? 'ticket.rates.passculture.fakeSeating.subtitle' : 'ticket.rates.seating.subtitle')}
                        rightComponent={
                            <Message type="tips" title={t('ticket.common.tips')} mini>
                                {t('ticket.rates.seating.tips')}
                            </Message>
                        }
                    >
                        <RateFormSeating
                            organizationId={organizationId}
                            event={event}
                            rate={rate}
                            form={formState}
                            formErrors={formErrors}
                            rateTemplates={rateTemplates}
                            onChangeForm={onChangeForm}
                            categories={categories}
                            inventories={inventories}
                            contingents={contingents}
                            charts={charts}
                            onChangeFormObject={onChangeFormObject}
                        />
                    </Card.FormStep>
                )}
                {RateClass === RateTemplate && !rate?.distributor && (
                    <Card.FormStep
                        title={t(`ticket.rates.settingEvents.title`)}
                        subtitle={t('ticket.rates.settingEvents.subtitle')}
                        rightComponent={
                            <Message type="tips" title={t('ticket.common.tips')} mini>
                                {t('ticket.rates.settingEvents.tips')}
                            </Message>
                        }
                    >
                        <RateFormSettingsEvents
                            organizationId={organizationId}
                            event={event}
                            rate={rate}
                            form={formState}
                            formErrors={formErrors}
                            onChangeForm={onChangeForm}
                            onChangeFormObject={onChangeFormObject}
                            onChangeEvents={onChangeEvents}
                            selectedEvents={selectedEvents}
                            events={events}
                        />
                    </Card.FormStep>
                )}
            </Card.FormManager>

            {RateClass !== RateTemplate && !rate?.distributor && (
                <OptionalForm
                    labelOpen={<span>{t(`ticket.rates.optionalSettings.titleClose`)}</span>}
                    labelClosed={<span>{t(`ticket.rates.optionalSettings.titleOpen`)}</span>}
                    displayAdditional
                    open={openOptionalForm}
                    onToggle={toggle => {
                        setOpenOptionalForm();
                        track('ACTION > DISPLAY ADVANCED OPTIONS', { 'Page Title': 'NEW RATE', Open: toggle });
                    }}
                >
                    <ReleaseCard
                        form={formState}
                        onChange={(key, value) => {
                            onChangeForm(key, value);
                        }}
                        onSetForm={form => {
                            onChangeFormObject(form, true);
                        }}
                        errors={formErrors}
                        event={event}
                        rates={rates}
                        rate={rate}
                        salesStart={salesStart}
                        onChangeSalesStart={setSalesStart}
                    />
                    <CartCard
                        form={formState}
                        onChange={(key, value) => {
                            onChangeForm(key, value);
                        }}
                        onSetForm={form => {
                            onChangeFormObject(form, true);
                        }}
                        rate={rate}
                        event={event}
                        errors={formErrors}
                    />
                    {cardViewPermissions.ticketManagement && (
                        <TicketManagement
                            organization={organization}
                            event={event}
                            rate={rate}
                            isAllowedTicketManagement={isAllowedTicketManagement}
                            form={formState}
                            setForm={onChangeForm}
                        />
                    )}
                </OptionalForm>
            )}
        </div>
    );
}

RateForm.propDefaults = {
    rateId: null,
    event: null
};

RateForm.propTypes = {
    organizationId: PropTypes.number.isRequired,
    event: PropTypes.object,
    rateId: PropTypes.string,
    history: PropTypes.object.isRequired,
    RateClass: PropTypes.func.isRequired,
    GroupClass: PropTypes.func.isRequired
};
